import PropTypes from 'prop-types';
import {
  FiArrowUpRight,
  FiBook,
  FiCheckCircle,
  FiEdit,
    FiDollarSign,
  FiEye,
  FiFile,
  FiInfo,
  FiKey,
  FiLogIn,
  FiPackage,
  FiPause,
  FiPocket,
  FiSlash,
  FiTag,
  FiTrash2,
  FiTruck,
  FiUserCheck,
  FiVideo,
  FiPlus,
} from 'react-icons/fi';

// The three standard row actions. `action` drives both the icon and the colour,
// so callers never hand-pick either — that's what keeps them uniform.
const ACTIONS = {
  view: { Icon: FiEye, label: 'View' },
  openTenant: { Icon: FiLogIn, label: 'Open tenant' },
  primaryAdmin: { Icon: FiUserCheck, label: 'Primary Admin' },
  edit: { Icon: FiEdit, label: 'Edit' },
  delete: { Icon: FiTrash2, label: 'Delete' },
  attachment: { Icon: FiFile, label: 'Attachments' },
  move: { Icon: FiArrowUpRight, label: 'Move Team' },
  history: { Icon: FiBook, label: 'Movement History' },
  payment: { Icon: FiDollarSign, label: 'Accept Payment' },
  viewEdit: { Icon: FiInfo, label: 'View/Edit' },
  complete: { Icon: FiCheckCircle, label: 'Mark completed' },
  // Same arrow glyph/colour as 'move', for actions that advance a row to its
  // next status (distinct from 'move', which is specifically for reassigning
  // a driver/helper's team).
  advance: { Icon: FiArrowUpRight, label: 'Advance' },
  wallet: { Icon: FiPocket, label: 'Wallet' },
  leave: { Icon: FiPause, label: 'Leave' },
  stock: { Icon: FiPackage, label: 'Stock' },
  serialNumber: { Icon: FiTag, label: 'Serial Number' },
  assignCar: { Icon: FiTruck, label: 'Assign Car' },
  assignBrand: { Icon: FiTag, label: 'Assign Brand' },
  video: { Icon: FiVideo, label: 'View Demo' },
  additionalServices: { Icon: FiPlus, label: 'Additional Services' },
  cancelled: { Icon: FiSlash, label: 'Cancelled' },
  resetPassword: { Icon: FiKey, label: 'Send password reset link' },
};

const ActionButton = ({ action, onClick, href, title, icon, showLabel, disabled, className, ...rest }) => {
  const preset = ACTIONS[action] || ACTIONS.view;
  const { Icon } = preset;
  const label = title || preset.label;
  const classes = `app-action-btn app-action-btn-${action} ${className}`;
  const glyph = icon || <Icon />;
  const content = showLabel ? <>{glyph}<span>{label}</span></> : glyph;

  // Some row actions navigate rather than act; keep them anchors so
  // middle-click and open-in-new-tab still work.
  if (href) {
    return (
      <a href={href} title={label} aria-label={label} className={classes} {...rest}>
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={classes}
      {...rest}
    >
      {content}
    </button>
  );
};

ActionButton.propTypes = {
  action: PropTypes.oneOf([
    'view',
    'openTenant',
    'primaryAdmin',
    'edit',
    'delete',
    'attachment',
    'move',
    'history',
    'viewEdit',
    'complete',
    'advance',
    'wallet',
    'leave',
    'stock',
    'serialNumber',
    'payment',
    'assignCar',
    'assignBrand',
    'video',
    'additionalServices',
    'cancelled',
  ]).isRequired,
  onClick: PropTypes.func,
  // Renders an <a> instead of a <button>, for actions that navigate.
  href: PropTypes.string,
  // Overrides the default tooltip/aria-label — use when "Edit" isn't accurate,
  // e.g. a destructive-looking button that actually changes status.
  title: PropTypes.string,
  // Escape hatch for a one-off glyph; keeps the action's colour and sizing.
  icon: PropTypes.node,
  showLabel: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

ActionButton.defaultProps = {
  onClick: undefined,
  href: '',
  title: '',
  icon: null,
  showLabel: false,
  disabled: false,
  className: '',
};

export default ActionButton;
